import React, { useState, useContext, useMemo, useEffect } from "react";
import Modal from "react-modal";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import Swal from "sweetalert2";
import Nav from "./Nav";
import moment from "moment";
import { UsuariosContext } from "../context/UsuariosContext";
import { useNavContext } from "../context/NavContext";

import Spinner from "./Spinner";

moment.locale("es");

// Colores según rol
const roleColors = {
  Administrador: "#dc3545",
  Ventas: "#38c172",
  Servicio: "#3490dc",
};

const PermisosUsuarios = () => {
  const usuarios = useContext(UsuariosContext);
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellido: "",
    email: "",
    rol: "Ventas",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Eliminar duplicados por email
  const usuariosUnicos = usuarios.reduce((acc, u) => {
    const email = u.email.trim().toLowerCase();
    if (!acc.find((x) => x.email.trim().toLowerCase() === email)) {
      acc.push({ ...u, email });
    }
    return acc;
  }, []);

  const paginatedUsuarios = useMemo(() => {
    return usuariosUnicos.slice();
  }, [usuariosUnicos]);

  useEffect(() => {
    if (Array.isArray(usuarios) && usuarios.length > 0) {
      setLoading(false);
    }
  }, [usuarios]);

  // MODAL
  const openModal = () => setModalIsOpen(true);
  const closeModal = () => {
    setModalIsOpen(false);
    setNuevoUsuario({ nombre: "", apellido: "", email: "", rol: "Ventas" });
  };
  const handleInput = (e) =>
    setNuevoUsuario({ ...nuevoUsuario, [e.target.name]: e.target.value });

  // GUARDAR / ACTUALIZAR
  const handleSaveUser = async () => {
    if (isSaving) return;
    setIsSaving(true);

    const { nombre, email, apellido, rol } = nuevoUsuario;
    if (!nombre.trim() || !email.trim()) {
      alert("Nombre y correo son requeridos.");
      setIsSaving(false);
      return;
    }

    const emailNorm = email.trim().toLowerCase();

    try {
      const q = query(
        collection(db, "usuarios"),
        where("email", "==", emailNorm)
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        // actualizar
        const docRef = snap.docs[0].ref;
        const dataPrev = snap.docs[0].data();

        await updateDoc(docRef, {
          nombre,
          apellido,
          email: emailNorm,
          rol,
          registrado: dataPrev.registrado ?? false,
        });

        Swal.fire(
          "¡Actualizado!",
          "Usuario actualizado correctamente.",
          "success"
        );
      } else {
        // crear + enviar invitación (vía Cloud Function + SendGrid)
        await addDoc(collection(db, "usuarios"), {
          nombre,
          apellido,
          email: emailNorm,
          rol,
          registrado: false,
        });

        // debe haber un usuario logueado para ownerUid
        if (!auth.currentUser?.uid) {
          Swal.fire(
            "Error",
            "Debes iniciar sesión para invitar usuarios.",
            "error"
          );
          setIsSaving(false);
          return;
        }

        // guarda el correo para que /registro lo recupere (mismo dispositivo)
        window.localStorage.setItem("inviteEmail", emailNorm);

        // Llamar a tu Cloud Function que genera el link y lo envía con SendGrid
        const functions = getFunctions(); // si tu CF está en otra región: getFunctions(undefined, "us-central1")
        const sendCustomInvite = httpsCallable(functions, "sendCustomInvite");
        await sendCustomInvite({
          email: emailNorm,
          ownerUid: auth.currentUser.uid,
          displayName: `${nombre} ${apellido}`.trim(),
          rol,
        });

        Swal.fire(
          "¡Invitación enviada!",
          "Revisa el correo del usuario.",
          "success"
        );
      }

      closeModal();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Ocurrió un problema guardando el usuario.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditUser = (u) => {
    setNuevoUsuario({ ...u, id: u.id });
    openModal();
  };

  const handleDeleteUser = async (u) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar usuario?",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (res.isConfirmed) {
      await deleteDoc(doc(db, "usuarios", u.id));
      Swal.fire("Eliminado", "Usuario eliminado correctamente.", "success");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* CONTENIDO */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "20px 10px 0 60px",
          }}
        >
          {/* CABECERA */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <h4 style={{ margin: 0 }}>Todos los usuarios</h4>
              <i className="bi bi-chevron-down" style={{ marginLeft: "6px" }} />
            </div>
            <button
              onClick={openModal}
              style={{
                padding: "8px 16px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Invitar a un usuario
            </button>
          </div>

          {/* GRID DE TARJETAS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "25px",
            }}
          >
            {paginatedUsuarios.map((u) => {
              const color = roleColors[u.rol] || "#ccc";
              return (
                <div
                  key={u.id}
                  style={{
                    display: "flex",
                    border: "1px solid #e0e0e0",
                    borderLeft: `4px solid ${color}`,
                    borderRadius: "8px",
                    padding: "16px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      marginRight: "12px",
                      fontSize: "2rem",
                      color: "#6c757d",
                    }}
                  >
                    <i className="bi bi-person-circle" />
                  </div>

                  {/* Contenido */}
                  <div style={{ flex: 1 }}>
                    {/* Header: Nombre + Acciones */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        {u.nombre} {u.apellido}
                      </span>
                      <div>
                        <i
                          className="bi bi-pencil-fill"
                          onClick={() => handleEditUser(u)}
                          style={{ cursor: "pointer", marginRight: "8px" }}
                          title="Editar"
                        />
                        <i
                          className="bi bi-trash-fill"
                          onClick={() => handleDeleteUser(u)}
                          style={{ cursor: "pointer", color: "#dc3545" }}
                          title="Eliminar"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "0.9rem",
                        marginBottom: "4px",
                      }}
                    >
                      <i
                        className="bi bi-envelope"
                        style={{ marginRight: "6px" }}
                      />
                      {u.email}
                    </div>

                    {/* Rol */}
                    <div style={{ fontWeight: 500, color }}>{u.rol}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* MODAL */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            border: "none",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "400px",
            width: "90%",
            maxHeight: "80vh",
            overflow: "auto",
          },
          overlay: { backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000 },
        }}
        ariaHideApp={false}
      >
        <button
          onClick={closeModal}
          style={{
            background: "none",
            border: "none",
            fontSize: "1.2rem",
            position: "absolute",
            top: "12px",
            right: "12px",
            cursor: "pointer",
          }}
        >
          <i className="bi bi-x-lg" />
        </button>

        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
          {nuevoUsuario.id ? "Editar usuario" : "Invitar usuario"}
        </h2>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <input
            name="nombre"
            placeholder="Nombre(s)"
            value={nuevoUsuario.nombre}
            onChange={handleInput}
            style={{
              flex: 1,
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
          <input
            name="apellido"
            placeholder="Apellido(s)"
            value={nuevoUsuario.apellido}
            onChange={handleInput}
            style={{
              flex: 1,
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        {/* Email */}
        <div style={{ marginBottom: "12px" }}>
          <input
            name="email"
            type="email"
            placeholder="Correo electrónico"
            value={nuevoUsuario.email}
            onChange={handleInput}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              boxSizing: "border-box", // ← clave
            }}
          />
        </div>

        {/* Rol */}
        <div style={{ marginBottom: "20px" }}>
          <select
            name="rol"
            value={nuevoUsuario.rol}
            onChange={handleInput}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              boxSizing: "border-box", // ← para que coincida
            }}
          >
            <option>Ventas</option>
            <option>Administrador</option>
            <option>Servicio</option>
          </select>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={handleSaveUser}
            disabled={isSaving}
            style={{
              flex: 1,
              padding: "10px",
              border: "none",
              borderRadius: "4px",
              backgroundColor: "#007bff",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Guardar
          </button>
          <button
            onClick={closeModal}
            style={{
              flex: 1,
              padding: "10px",
              border: "none",
              borderRadius: "4px",
              backgroundColor: "#6c757d",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      </Modal>
    </>
  );
};

export default PermisosUsuarios;
